/**
 * nightPlanner.ts — "Plan my nights".
 *
 * Lays a trip out day by day from the stops, their nights, the route legs and
 * the daily driving budget:
 *
 *   Melbourne → Halls Gap (2 nights) → Mount Gambier (1 night) → Adelaide, 6h/day
 *   Day 1  drive Melbourne → Halls Gap        night 1 at Halls Gap
 *   Day 2  rest day                            night 2 at Halls Gap
 *   Day 3  drive Halls Gap → Mount Gambier     night 3 at Mount Gambier
 *   Day 4  drive Mount Gambier → Adelaide      arrive
 *
 * Any stretch between two places you sleep that's longer than the budget gets
 * evenly spaced SUGGESTED overnights (not yet in the trip) so no day runs over.
 * Pure — the caller supplies a `pointAt` that finds a spot along the routed
 * polyline; without one we interpolate between the stops.
 */

import type { TripLeg, TripStop } from '../types/trip';
import { dateForTripDay } from './tripPlanner';
import { interpolate, roundToTotal, type LatLngPoint } from './routeLegs';

export interface NightSlot {
  /** Stable id: the stop id, or `${fromStopId}>${toStopId}#n` for a suggestion. */
  key: string;
  /** First night number spent here (1-based). */
  night: number;
  /** Nights spent here. */
  nights: number;
  /** 0-based day you arrive. */
  dayIndex: number;
  /** Arrival date (YYYY-MM-DD) when the trip has a start date. */
  date: string | null;
  name: string;
  lat?: number;
  lng?: number;
  /** The existing overnight/camp stop, when this isn't a suggestion. */
  stopId?: string;
  /** The campsite already picked for this night (camp stops). */
  entityId?: number;
  /** A halfway overnight we're suggesting — not in the trip yet. */
  suggested: boolean;
  /** Where a suggested overnight would be inserted. */
  insertAfterStopId?: string;
}

export interface PlanDay {
  index: number;
  date: string | null;
  kind: 'drive' | 'rest';
  fromName: string;
  toName: string;
  viaNames: string[];
  distanceKm?: number;
  driveMinutes?: number;
  /** Trip-minute window this day's drive covers (for grouping camps by day). */
  startMinute: number;
  endMinute: number;
  /** Where you sleep tonight; null on the day you arrive at the destination. */
  slot: NightSlot | null;
}

export interface NightPlan {
  days: PlanDay[];
  slots: NightSlot[];
  totalNights: number;
  /** Cumulative drive minutes at each stop (index-aligned with stops). */
  stopMinutes: number[];
}

export interface PlanNightsInput {
  stops: TripStop[];
  legs: TripLeg[];
  maxDriveMinutes: number;
  startDate?: string | null;
  /** A point `fraction` (by distance/time) of the way along leg `legIndex`. */
  pointAt?: (legIndex: number, fraction: number) => LatLngPoint | null;
}

/** Name of a suggested overnight until we learn the nearest town. */
export const SUGGESTED_STOP_NAME = 'Overnight on the way';

/** "around Horsham" once named; the placeholder reads fine on its own. */
export function suggestedSlotLabel(name: string): string {
  return name === SUGGESTED_STOP_NAME ? 'Suggested overnight on the way' : `Suggested stop around ${name}`;
}

const EMPTY: NightPlan ={ days: [], slots: [], totalNights: 0, stopMinutes: [] };

function legFigures(stops: TripStop[], legs: TripLeg[]) {
  const byFrom = new Map(legs.map((l) => [l.fromId, l]));
  const minutes: number[] = [];
  const km: number[] = [];
  for (let i = 0; i < stops.length - 1; i += 1) {
    const l = byFrom.get(stops[i].id);
    minutes.push(typeof l?.driveMinutes === 'number' ? l.driveMinutes : 0);
    km.push(typeof l?.distanceKm === 'number' ? l.distanceKm : 0);
  }
  return { minutes, km };
}

function hasCoords(s: TripStop): s is TripStop & { lat: number; lng: number } {
  return typeof s.lat === 'number' && typeof s.lng === 'number';
}

export function planNights({
  stops,
  legs,
  maxDriveMinutes,
  startDate = null,
  pointAt,
}: PlanNightsInput): NightPlan {
  if (stops.length < 2) return EMPTY;
  const { minutes, km } = legFigures(stops, legs);
  const stopMinutes = [0];
  for (const m of minutes) stopMinutes.push(stopMinutes[stopMinutes.length - 1] + m);

  // Where you sleep: the start (you leave from it), every middle stop with
  // nights, and the end.
  const last = stops.length - 1;
  const sleepIdx = [0];
  for (let i = 1; i < last; i += 1) {
    if ((stops[i].nights ?? 0) >= 1) sleepIdx.push(i);
  }
  sleepIdx.push(last);

  const days: PlanDay[] = [];
  const slots: NightSlot[] = [];
  let night = 0;

  const pushNightDays = (slot: NightSlot) => {
    // The drive day already holds the first night; the rest are rest days.
    for (let n = 1; n < slot.nights; n += 1) {
      night += 1;
      const index = days.length;
      days.push({
        index,
        date: dateForTripDay(startDate, index),
        kind: 'rest',
        fromName: slot.name,
        toName: slot.name,
        viaNames: [],
        startMinute: days[index - 1]?.endMinute ?? 0,
        endMinute: days[index - 1]?.endMinute ?? 0,
        slot,
      });
    }
  };

  for (let s = 0; s < sleepIdx.length - 1; s += 1) {
    const a = sleepIdx[s];
    const b = sleepIdx[s + 1];
    const segStart = stopMinutes[a];
    const segMinutes = stopMinutes[b] - segStart;
    const segKm = km.slice(a, b).reduce((x, y) => x + y, 0);
    const extra =
      maxDriveMinutes > 0 && segMinutes > maxDriveMinutes
        ? Math.ceil(segMinutes / maxDriveMinutes) - 1
        : 0;
    const pieces = extra + 1;
    const pieceMinutes = roundToTotal(new Array(pieces).fill(1), segMinutes);
    const pieceKm = roundToTotal(new Array(pieces).fill(1), segKm);

    let pieceStart = segStart;
    let fromName = stops[a].name;
    for (let p = 0; p < pieces; p += 1) {
      const pieceEnd = p === pieces - 1 ? stopMinutes[b] : pieceStart + pieceMinutes[p];
      const viaNames = stops
        .slice(a + 1, b)
        .filter((_, k) => {
          const m = stopMinutes[a + 1 + k];
          return m > pieceStart && m <= pieceEnd;
        })
        .map((v) => v.name);

      let slot: NightSlot | null = null;
      const dayIndex = days.length;
      if (p < pieces - 1) {
        // Suggested halfway overnight at minute `pieceEnd`.
        let leg = a;
        while (leg < b - 1 && stopMinutes[leg + 1] < pieceEnd) leg += 1;
        const legMin = minutes[leg] || 1;
        const fraction = Math.max(0, Math.min(1, (pieceEnd - stopMinutes[leg]) / legMin));
        const from = stops[leg];
        const to = stops[leg + 1];
        const point =
          pointAt?.(leg, fraction) ??
          (hasCoords(from) && hasCoords(to) ? interpolate(from, to, fraction) : null);
        night += 1;
        slot = {
          key: `${stops[a].id}>${stops[b].id}#${p + 1}`,
          night,
          nights: 1,
          dayIndex,
          date: dateForTripDay(startDate, dayIndex),
          name: SUGGESTED_STOP_NAME,
          lat: point?.lat,
          lng: point?.lng,
          suggested: true,
          insertAfterStopId: from.id,
        };
      } else if (b !== last) {
        const stop = stops[b];
        night += 1;
        slot = {
          key: stop.id,
          night,
          nights: Math.max(1, stop.nights ?? 1),
          dayIndex,
          date: dateForTripDay(startDate, dayIndex),
          name: stop.name,
          lat: stop.lat,
          lng: stop.lng,
          stopId: stop.id,
          entityId: stop.kind === 'camp' ? stop.entityId : undefined,
          suggested: false,
        };
      }

      const toName = slot ? slot.name : stops[b].name;
      days.push({
        index: dayIndex,
        date: dateForTripDay(startDate, dayIndex),
        kind: 'drive',
        fromName,
        toName,
        viaNames,
        distanceKm: pieceKm[p],
        driveMinutes: pieceMinutes[p],
        startMinute: pieceStart,
        endMinute: pieceEnd,
        slot,
      });
      if (slot) {
        slots.push(slot);
        pushNightDays(slot);
      }
      fromName = toName;
      pieceStart = pieceEnd;
    }
  }

  return { days, slots, totalNights: night, stopMinutes };
}

/**
 * Which plan day a point at trip-minute `minute` falls on (drive days only).
 * Used to group "camps along route" by day.
 */
export function dayForMinute(plan: NightPlan, minute: number): PlanDay | null {
  const drives = plan.days.filter((d) => d.kind === 'drive');
  if (!drives.length) return null;
  for (const d of drives) {
    if (minute <= d.endMinute) return d;
  }
  return drives[drives.length - 1];
}

/** Rename suggested slots (e.g. after we learn the nearest town). */
export function withSlotNames(plan: NightPlan, names: Record<string, string>): NightPlan {
  if (!Object.keys(names).length) return plan;
  const rename = (slot: NightSlot | null): NightSlot | null =>
    slot && slot.suggested && names[slot.key] ? { ...slot, name: names[slot.key] } : slot;
  const slots = plan.slots.map((s) => rename(s)!) as NightSlot[];
  const byKey = new Map(slots.map((s) => [s.key, s]));
  const days = plan.days.map((d, i, all) => {
    const slot = d.slot ? byKey.get(d.slot.key) ?? d.slot : null;
    const prev = all[i - 1];
    const prevSlot = prev?.slot ? byKey.get(prev.slot.key) ?? prev.slot : null;
    return {
      ...d,
      slot,
      toName: d.kind === 'drive' && slot ? slot.name : d.kind === 'rest' && slot ? slot.name : d.toName,
      fromName:
        d.kind === 'rest' && slot
          ? slot.name
          : prevSlot && prevSlot.suggested
            ? prevSlot.name
            : d.fromName,
    };
  });
  return { ...plan, slots, days };
}
